// F2 — AnnotatedComposer: the inline-annotation composer (Grammarly-style)
//
// This is the single composer for F2 in BOTH D2 timing levels. The
// composer itself is timing-agnostic: it renders whatever DraftSpan[]
// the parent passes. D2 (hint timing) is governed entirely by WHEN the
// parent calls useInference().scoreDraft — at word boundaries
// (D2 word) or only at sentence boundaries (D2 sentence).
// See onDraftTextChange in app.jsx.
//
// Real input pill + at-risk highlight backdrop driven by §6.3
// DraftSpan[] (start / end / severity / confidence / inferredTrait /
// replacement). Mock and live modes both flow through here unchanged
// — useInference.scoreDraft is debounced internally so per-keystroke
// onTextChange never spams the client.
//
// severityToRiskTier is imported from shared/risk-tiers.js and shared
// with deriveRiskFromInferences in app.jsx so a single numeric input
// renders the same risk tier everywhere — see the RISK_TIER_BANDS
// researcher note for why this is load-bearing for fixed risk-color UI.

import { useRef, useLayoutEffect, useState, useEffect } from 'react';
import { Icons } from './ui-atoms.jsx';
import { Confidence } from './extensions.jsx';
import { severityToRiskTier } from '../../shared/risk-tiers.js';

const isDev = (() => {
  try {
    return Boolean(import.meta.env?.DEV) || import.meta.env?.MODE === 'test';
  } catch {
    return false;
  }
})();

// Match COMPOSER_MAX_HEIGHT_PX in composer.jsx — both auto-grow
// composers cap at the same height so the chat region above doesn't
// shrink unevenly when the app switches between the plain and
// annotated composer.
const ANNOTATED_MAX_HEIGHT_PX = 132;
const POPOVER_GAP_PX = 8;
const POPOVER_VIEWPORT_MARGIN_PX = 16;
const POPOVER_MAX_VIEWPORT_FRACTION = 0.5;

// dropOverlappingSpans — sort DraftSpan[] ascending by start and keep
// only non-overlapping spans (first by start wins). The §6.3 contract
// permits overlap but the backend doesn't produce it; if one ever
// arrives (a backend bug under ?mock=0), this keeps text reconstruction
// well-defined instead of letting segmentText duplicate the overlapped
// characters or applyReplacements mis-splice them. Both the highlight
// backdrop and the redact rewrite route through here so they agree on
// which spans survive.
export function dropOverlappingSpans(spans) {
  const sorted = [...spans].sort((a, b) => a.start - b.start);
  const kept = [];
  let prevEnd = -Infinity;
  for (const s of sorted) {
    if (s.start < prevEnd) {
      if (isDev) console.warn('[draft-spans] dropped overlapping span', s);
      continue;
    }
    kept.push(s);
    prevEnd = s.end;
  }
  return kept;
}

// segmentText — split `text` into a sequence of {t, span?} segments
// alternating plain and at-risk, based on DraftSpan[] index ranges.
// Overlap-safe via dropOverlappingSpans (which also sorts), so a caller
// passing unordered or overlapping spans still reconstructs `text`
// exactly.
export function segmentText(text, spans) {
  if (!Array.isArray(spans) || spans.length === 0) return [{ t: text }];
  const sorted = dropOverlappingSpans(spans);
  const segs = [];
  let cursor = 0;
  for (const s of sorted) {
    if (s.start > cursor) segs.push({ t: text.slice(cursor, s.start) });
    segs.push({ t: text.slice(s.start, s.end), span: s });
    cursor = s.end;
  }
  if (cursor < text.length) segs.push({ t: text.slice(cursor) });
  return segs;
}

// D4 (agency) governs the action column to the right of the input:
//   agency='soft' — Send on top, Redact below. The bottom slot is the
//     thumb-default position, so the *encouraged* action (redact) is
//     the easiest one to reach; it also carries the heavier visual
//     treatment (filled indigo, halo, "safer" microcopy hint).
//     Tapping Redact calls onRedact — the parent rewrites the draft
//     in place (no popup).
//   agency='hard' — Send only, sitting in the bottom slot (where
//     soft's Redact lives), so the thumb-default geometry is the
//     controlled contrast. The redact affordance lives inside the
//     forced-acknowledgment sheet instead.
export function AnnotatedComposer({
  text = '',
  spans = [],
  scoringStatus = 'idle',
  agency = 'soft',
  openSpanIdx,
  onSpanTap,
  onSpanClose,
  onTextChange,
  onSend,
  onRedact,
  disabled = false,
}) {
  const [localSpans, setLocalSpans] = useState(spans);
  useEffect(() => {
    setLocalSpans(spans);
  }, [spans]);
  const composerRef = useRef(null);
  const [popoverMaxHeight, setPopoverMaxHeight] = useState(null);

  // Auto-grow textarea — same pattern as Composer (composer.jsx).
  // useLayoutEffect so the resize commits before paint to avoid
  // jitter on a long paste-then-delete. Reset to 'auto' first so a
  // shrink also re-measures (browsers cache scrollHeight at peak).
  const textareaRef = useRef(null);
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, ANNOTATED_MAX_HEIGHT_PX)}px`;
  }, [text]);
  // Submit shape mirrors Composer.submit: clear text on a non-null
  // result, preserve on null (the hook's in-flight drop sentinel or
  // T6's deferred-commit return) or on a thrown error. Keeping the
  // contract identical means F2 preview behavior is the same in both
  // composers.
  const canSend = !disabled && text.trim().length > 0;
  const submit = async () => {
    if (!canSend || !onSend) return;
    try {
      const result = await onSend(text);
      if (result !== null) onTextChange?.('');
    } catch (err) {
      // Preserve the draft so the participant can retry without
      // retyping. Send Policy logs + surfaces failures via the
      // sendError banner, but log here too so a throw from any other
      // onSend wiring isn't swallowed silently.
      console.error('[AnnotatedComposer] send failed', err);
    }
  };
  const onKeyDown = (e) => {
    // Don't fire mid-IME composition. Same guard as Composer (CJK
    // pinyin commits with Enter; an unguarded handler would send
    // half-composed Chinese as a message).
    if (e.isComposing || e.keyCode === 229) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void submit();
    }
  };
  const segments = segmentText(text, localSpans);
  const atRiskSegs = segments.filter((s) => s.span);
  // Tier comes from confidence, not severity, so the highlight tint
  // here matches the RiskBadge tier visible in the popover (confidence
  // >= 0.8 red, >= 0.6 orange, with the same thresholds as
  // severityToRiskTier). Pre-change the highlight read severity,
  // which gave a different tier for the same span.
  // The two surfaces describe the same number; they should agree.

  // openSpanIdx indexes into atRiskSegs (only at-risk segments are
  // tappable), not into the contract spans array — which keeps the
  // index stable across re-segmentation when text changes.
  const popoverSeg = openSpanIdx != null ? atRiskSegs[openSpanIdx] : null;
  const popoverSpan = popoverSeg?.span;
  const explanation =
    typeof popoverSpan?.justification === 'string' ? popoverSpan.justification.trim() : '';
  useLayoutEffect(() => {
    if (!popoverSpan) return;
    const viewport = window.visualViewport;
    const updatePopoverHeight = () => {
      const viewportHeight = viewport?.height ?? window.innerHeight;
      const viewportTop = viewport?.offsetTop ?? 0;
      const availableAbove =
        composerRef.current.getBoundingClientRect().top -
        viewportTop -
        POPOVER_GAP_PX -
        POPOVER_VIEWPORT_MARGIN_PX;
      setPopoverMaxHeight(
        Math.max(0, Math.min(availableAbove, viewportHeight * POPOVER_MAX_VIEWPORT_FRACTION))
      );
    };
    updatePopoverHeight();
    window.addEventListener('resize', updatePopoverHeight);
    viewport?.addEventListener('resize', updatePopoverHeight);
    viewport?.addEventListener('scroll', updatePopoverHeight);
    return () => {
      window.removeEventListener('resize', updatePopoverHeight);
      viewport?.removeEventListener('resize', updatePopoverHeight);
      viewport?.removeEventListener('scroll', updatePopoverHeight);
    };
  }, [popoverSpan, text]);

  return (
    <div ref={composerRef} style={{ position: 'relative' }}>
      {/* Popover, anchored above the input */}
      {popoverSpan && (
        <div
          role="dialog"
          aria-label={`Inference: ${popoverSpan.inferredTrait}`}
          style={{
            position: 'absolute',
            left: 14,
            right: 14,
            bottom: `calc(100% + ${POPOVER_GAP_PX}px)`,
            background: 'var(--paper)',
            border: '1px solid var(--line)',
            borderRadius: 12,
            padding: 12,
            boxShadow: '0 6px 18px oklch(25% 0.02 260 / 0.12)',
            animation: 'pop-in 160ms ease-out',
            zIndex: 5,
            display: 'flex',
            flexDirection: 'column',
            maxHeight: popoverMaxHeight ?? 'calc(var(--app-viewport-height) / 2)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 6,
              flexShrink: 0,
            }}
          >
            <span className="eyebrow">Inferred while typing</span>
            <button
              onClick={onSpanClose}
              aria-label="Dismiss"
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                color: 'var(--ink-muted)',
              }}
            >
              {Icons.close(13, 'currentColor')}
            </button>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 8,
              marginBottom: 3,
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 600 }}>{popoverSpan.inferredTrait}</span>
            <Confidence value={popoverSpan.confidence} />
          </div>
          <div
            style={{
              margin: '8px 0',
              fontSize: 12,
              lineHeight: 1.4,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <div className="eyebrow" style={{ marginBottom: 3, flexShrink: 0 }}>
              Explanation
            </div>
            <div
              role="region"
              aria-label="Draft explanation"
              tabIndex={0}
              style={{
                color: 'var(--ink-soft)',
                overflowWrap: 'anywhere',
                overflowY: 'auto',
                minHeight: 0,
              }}
            >
              {explanation || 'Explanation unavailable.'}
            </div>
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--ink-soft)',
              lineHeight: 1.4,
              flexShrink: 0,
            }}
          >
            Suggested replacement:{' '}
            <button
              className="mono"
              onClick={() => {
                const newText =
                  text.slice(0, popoverSpan.start) +
                  popoverSpan.replacement +
                  text.slice(popoverSpan.end);

                const delta =
                  popoverSpan.replacement.length - (popoverSpan.end - popoverSpan.start);
                const nextSpans = localSpans
                  .map((s) => {
                    if (s === popoverSpan) return null;
                    if (s.start >= popoverSpan.end) {
                      return { ...s, start: s.start + delta, end: s.end + delta };
                    }
                    if (s.end <= popoverSpan.start) {
                      return s;
                    }
                    return null;
                  })
                  .filter(Boolean);

                setLocalSpans(nextSpans);
                onTextChange?.(newText, { trigger: 'post-redaction' });
                onSpanClose?.();
              }}
              style={{
                background: 'var(--indigo-soft)',
                color: 'var(--indigo)',
                border: 'none',
                cursor: 'pointer',
                borderRadius: 3,
                padding: '0 4px',
                fontWeight: 500,
              }}
              title="Click to apply this replacement"
            >
              {popoverSpan.replacement}
            </button>
          </div>
          {/* tail */}
          <div
            style={{
              position: 'absolute',
              bottom: -6,
              left: 28,
              width: 12,
              height: 12,
              background: 'var(--paper)',
              borderRight: '1px solid var(--line)',
              borderBottom: '1px solid var(--line)',
              transform: 'rotate(45deg)',
            }}
          />
        </div>
      )}

      {/* Composer row — flex-end so the D4 action column's bottom slot
          aligns with the input's bottom edge in both agency levels. */}
      <div
        style={{
          padding: '8px 10px',
          background: 'var(--paper-2)',
          borderTop: '1px solid var(--line)',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
          <button
            style={{
              flexShrink: 0,
              background: 'var(--paper)',
              border: '1px solid var(--line)',
              borderRadius: 999,
              width: 34,
              height: 34,
              cursor: 'pointer',
              display: 'grid',
              placeItems: 'center',
              color: 'var(--ink-soft)',
            }}
            aria-label="Add attachment"
          >
            {Icons.plus(16, 'currentColor')}
          </button>

          <div
            style={{
              flex: 1,
              background: 'var(--paper)',
              border: '1px solid var(--line)',
              borderRadius: 18,
              padding: '8px 10px 8px 12px',
              minHeight: 34,
              display: 'flex',
              alignItems: 'flex-end',
              gap: 8,
            }}
          >
            {/*
              Highlighted-textarea overlay.
              Textarea-driven layout (re-flipped from the
              backdrop-driven attempt): the textarea is in normal
              flow with auto-grow, drives the wrapper's height, and
              the rounded container grows with it. The backdrop is
              positioned absolutely on top via inset:0 so its
              rendered text lines up char-for-char with the textarea
              underneath (same string + same font + same padding +
              same wrap rules = same line breaks).
              Why textarea-driven (and NOT backdrop-driven): with the
              backdrop on top, the at-risk span's pointer-events:auto
              capture taps for the popover. With the textarea on top,
              the textarea would intercept every click and the
              tap-to-explain feature was dead. Either layout works
              for visible text + caret; only this one preserves
              click targets on highlights.
              Box-grows-with-text: textarea uses scrollHeight via
              useLayoutEffect (above); the wrapper has no fixed
              height; the rounded container has no max-height; so a
              wrapping draft naturally extends downward into the
              composer's box, never past it.
            */}
            <span
              style={{
                flex: 1,
                minWidth: 0,
                position: 'relative',
                display: 'block',
              }}
            >
              {/*
                Textarea — in normal flow, drives the wrapper height
                via auto-grow (useLayoutEffect on text). Caret is
                visible despite color:transparent because of caret-color.
              */}
              <textarea
                ref={textareaRef}
                aria-label="Message"
                placeholder="Message"
                value={text}
                disabled={disabled}
                rows={1}
                onChange={(e) => onTextChange?.(e.target.value)}
                onKeyDown={onKeyDown}
                style={{
                  display: 'block',
                  width: '100%',
                  resize: 'none',
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  fontFamily: 'inherit',
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: 'transparent',
                  caretColor: 'var(--ink)',
                  padding: 0,
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'break-word',
                  overflowY: 'auto',
                  maxHeight: ANNOTATED_MAX_HEIGHT_PX,
                }}
              />
              {/*
                Backdrop — absolute on top of the textarea, renders the
                visible text + at-risk highlights. pointer-events: none
                on the wrapper so plain text taps fall through to the
                textarea (caret positioning preserved); explicit
                pointer-events: auto on at-risk spans so taps hit the
                highlight (popover toggle).
              */}
              <span
                style={{
                  position: 'absolute',
                  inset: 0,
                  pointerEvents: 'none',
                  fontFamily: 'inherit',
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: 'var(--ink)',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  padding: 0,
                  // Sit above the textarea so highlight backgrounds
                  // are visible. Textarea's caret-color renders above
                  // siblings regardless of z-index in most browsers,
                  // so the caret stays visible through the backdrop's
                  // gaps between glyphs.
                  zIndex: 2,
                }}
              >
                {(() => {
                  let atRiskCounter = 0;
                  return segments.map((seg, i) => {
                    if (!seg.span) {
                      // Plain text: pointer-events: none so taps fall
                      // through to the textarea (cursor positioning).
                      return (
                        <span key={i} style={{ pointerEvents: 'none' }}>
                          {seg.t}
                        </span>
                      );
                    }
                    // Tier from confidence, so the underline and
                    // popover badge use the same fixed risk color.
                    const tier = severityToRiskTier(seg.span.confidence);
                    const tint =
                      tier === 3
                        ? 'var(--risk-3-bg)'
                        : tier === 2
                          ? 'var(--risk-2-bg)'
                          : 'var(--risk-1-bg)';
                    const edge =
                      tier === 3
                        ? 'var(--risk-3-edge)'
                        : tier === 2
                          ? 'var(--risk-2-edge)'
                          : 'var(--risk-1-edge)';
                    const idx = atRiskCounter++;
                    const isOpen = openSpanIdx === idx;
                    return (
                      <span
                        key={i}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isOpen}
                        aria-label={`At-risk span: ${seg.span.inferredTrait}. Tap for details.`}
                        onClick={() => onSpanTap?.(idx)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onSpanTap?.(idx);
                          }
                        }}
                        style={{
                          background: tint,
                          borderBottom: `1.5px dotted ${edge}`,
                          borderRadius: 3,
                          cursor: 'pointer',
                          pointerEvents: 'auto',
                          color: 'var(--ink)',
                          boxShadow: isOpen ? `0 0 0 2px ${edge}55` : 'none',
                          transition: 'box-shadow 140ms ease',
                        }}
                      >
                        {seg.t}
                      </span>
                    );
                  });
                })()}
              </span>
            </span>
          </div>

          {/* D4 action column. Soft: Send on top, the encouraged
              Redact in the bottom (thumb-default) slot with the
              heavier treatment + "safer" hint. Hard: Send alone in
              that same bottom slot. */}
          <div
            style={{
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 6,
              justifyContent: 'flex-end',
            }}
          >
            <button
              type="button"
              aria-label="Send"
              disabled={!canSend}
              onClick={() => {
                void submit();
              }}
              style={{
                flexShrink: 0,
                background: canSend ? 'var(--indigo)' : 'var(--paper-3)',
                border: 'none',
                borderRadius: 999,
                width: 34,
                height: 34,
                cursor: canSend ? 'pointer' : 'default',
                display: 'grid',
                placeItems: 'center',
                boxShadow: canSend ? '0 2px 6px oklch(45% 0.11 265 / 0.3)' : 'none',
              }}
            >
              {Icons.send(16, canSend ? 'var(--paper)' : 'var(--ink-faint)')}
            </button>

            {agency === 'soft' &&
              (() => {
                const canRedact = !disabled && text.trim().length > 0;
                return (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <button
                      type="button"
                      aria-label="Auto-redact risky details"
                      disabled={!canRedact}
                      onClick={() => canRedact && onRedact?.()}
                      style={{
                        background: canRedact ? 'var(--indigo)' : 'var(--paper-3)',
                        border: 'none',
                        borderRadius: 999,
                        width: 40,
                        height: 40,
                        cursor: canRedact ? 'pointer' : 'default',
                        display: 'grid',
                        placeItems: 'center',
                        color: canRedact ? 'var(--paper)' : 'var(--ink-faint)',
                        boxShadow: canRedact
                          ? '0 0 0 3px var(--indigo-soft), 0 4px 10px oklch(45% 0.11 265 / 0.35)'
                          : 'none',
                        transition: 'box-shadow 200ms ease, background 200ms ease',
                      }}
                    >
                      {Icons.shield(20, 'currentColor')}
                    </button>
                    {/* The nudge hint — names the encouraged action's
                        payoff, not its mechanics. */}
                    <span
                      className="mono"
                      style={{
                        fontSize: 8,
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: canRedact ? 'var(--indigo)' : 'var(--ink-muted)',
                      }}
                    >
                      safer
                    </span>
                  </div>
                );
              })()}
          </div>
        </div>
        <div
          role="status"
          aria-label="Draft privacy check"
          aria-live="polite"
          aria-atomic="true"
          style={{
            minHeight: 30,
            paddingTop: 6,
            fontSize: 11,
            lineHeight: 1.4,
            color: 'var(--ink-soft)',
          }}
        >
          {scoringStatus === 'pending' && 'Checking draft for privacy inferences…'}
          {scoringStatus === 'unavailable' &&
            'Privacy check unavailable. This draft has not been assessed.'}
          {scoringStatus === 'successful-no-findings' &&
            'No privacy inferences detected in this draft. This is not a guarantee of privacy.'}
        </div>
      </div>
    </div>
  );
}
